// Sui zkLogin 工具函數
import { Ed25519Keypair } from "@mysten/sui/keypairs/ed25519";
import { getFullnodeUrl, SuiClient } from "@mysten/sui/client";
import { jwtToAddress, generateRandomness, generateNonce, getExtendedEphemeralPublicKey } from '@mysten/sui/zklogin';
import { getOrSetZkLoginSalt } from '../api/api'; // 假設這是你的 API 函數
import { jwtDecode } from 'jwt-decode';
import { Buffer } from 'buffer';
import { decodeSuiPrivateKey } from "@mysten/sui/cryptography";

/**
 * Sui zkLogin 服務類
 */
export class SuiZkLoginService {
    constructor(options = {}) {
        this.mockMode = options.mockMode || false;
        this.network = options.network || 'testnet'; // devnet, testnet, mainnet
        this.clientId = options.clientId;
        this.redirectUri = options.redirectUri;
        this.suiClient = new SuiClient({ url: getFullnodeUrl(this.network) });
    }

    // step 2. startZkLoginFlow
    async startZkLoginFlow() {

        const suiClient = new SuiClient({ url: getFullnodeUrl(this.network) });
        const { epoch, epochDurationMs, epochStartTimestampMs } = await suiClient.getLatestSuiSystemState();

        // Step 1: Get current Sui epoch and generate nonce
        const maxEpoch = Number(epoch) + 2; // this means the ephemeral key will be active for 2 epochs from now.
        const ephemeralKeyPair = Ed25519Keypair.generate();
        const randomness = generateRandomness();
        const nonce = generateNonce(ephemeralKeyPair.getPublicKey(), maxEpoch, randomness);

        // Step 2: Store session data using the correct property access
        // The secretKey is a Uint8Array, store it as base64 to be safe.
        // const secretKeyB64 = Buffer.from(ephemeralKeyPair.keypair.secretKey).toString('base64');
        sessionStorage.setItem('zklogin_ephemeral_secret_key', ephemeralKeyPair.getSecretKey());
        sessionStorage.setItem('zklogin_ephemeral_public_key', Buffer.from(ephemeralKeyPair.keypair.publicKey).toString('base64')); // For the prover
        sessionStorage.setItem('zklogin_max_epoch', maxEpoch.toString());
        sessionStorage.setItem('zklogin_jwt_randomness', randomness);

        // Step 3: Create Google OAuth URL
        const params = new URLSearchParams({
            client_id: this.clientId,
            redirect_uri: this.redirectUri,
            response_type: 'id_token',
            scope: 'openid',
            nonce: nonce,
        });

        console.log('params', params);

        const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;

        console.log('redirectUri', this.redirectUri);
        console.log('Generated Google OAuth URL with nonce:', nonce);
        return authUrl.toString();
    }

    // step 3. handleOAuthCallback
    async handleOAuthCallback(idToken) {
        try {
            console.log('Processing OAuth callback with JWT:', idToken?.substring(0, 50) + '...');

            // Step 1: Decode JWT
            const decodedJwt = jwtDecode(idToken);
            sessionStorage.setItem('id_token', idToken);
            console.log('Decoded JWT:', decodedJwt);

            // Step 2: Retrieve stored session data
            const zkSecretKey = sessionStorage.getItem('zklogin_ephemeral_secret_key');

            const tempKeyPair = decodeSuiPrivateKey(zkSecretKey);
            const keypair = Ed25519Keypair.fromSecretKey(tempKeyPair.secretKey);

            // const keypair = Ed25519Keypair.fromSecretKey(Buffer.from(zkSecretKey, 'base64'));


            const storedMaxEpoch = sessionStorage.getItem('zklogin_max_epoch');
            const storedJwtRandomness = sessionStorage.getItem('zklogin_jwt_randomness');

            if (!storedMaxEpoch || !storedJwtRandomness) {
                throw new Error('Missing session data for zkLogin');
            }

            // Step 3: Get salt from salt service (if using external service)
            const finalSalt = await this.getSaltFromService(idToken);

            // Step 4: Generate user address using Sui SDK
            const zkLoginUserAddress = jwtToAddress(idToken, BigInt(finalSalt));
            console.log('Generated Sui address:', zkLoginUserAddress);

            const extendedEphemeralPublicKey = getExtendedEphemeralPublicKey(keypair.getPublicKey());
            const zkProof = await this.getPartialZkLoginSignature(
                extendedEphemeralPublicKey,
                idToken,
                decodedJwt,
                finalSalt,
                storedMaxEpoch,
                storedJwtRandomness);

            console.log('ZK proof generated successfully');

            return {
                success: true,
                jwt: idToken,
                zkLoginUserAddress,
                ephemeralPublicKey: extendedEphemeralPublicKey,
                decodedJwt,
                zkProof,
                maxEpoch: parseInt(storedMaxEpoch),
                jwtRandomness: storedJwtRandomness
            };
        } catch (error) {
            console.error('OAuth callback handling failed:', error);
            throw error;
        }
    }

    async getZkProof(params) {

        try {
            // Real ZK proving service call to Mysten Labs
            const zkProverUrl = 'https://prover-dev.mystenlabs.com/v1';

            const requestBody = {
                maxEpoch: params.maxEpoch,
                jwtRandomness: params.jwtRandomness,
                extendedEphemeralPublicKey: params.ephemeralPublicKey,
                jwt: params.jwt,
                salt: params.userSalt,
                keyClaimName: 'sub', // Usually 'sub' for Google
            };

            console.log('Sending request to ZK prover service...');

            const response = await fetch(zkProverUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody, null, 2)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`ZK proof generation failed: ${response.status} ${response.statusText} - ${errorText}`);
            }

            const proof = await response.json();
            console.log('ZK proof received successfully');
            return proof;

        } catch (error) {
            console.error('ZK proof generation error:', error);
            throw new Error(`ZK proof generation failed: ${error.message}`);
        }
    }

    async getSaltFromService(idToken) {
        let userSalt = await getOrSetZkLoginSalt({ idToken: idToken, system: '20SUI' });

        if (userSalt) {
            sessionStorage.setItem('zklogin_user_salt', userSalt.salt);
            return userSalt.salt;
        }

        return null;
    }

    async getPartialZkLoginSignature(extendedEphemeralPublicKey, idToken, decodedJwt, finalSalt, storedMaxEpoch, storedJwtRandomness) {

        // Step 5: Get ZK proof from proving service
        const zkProof = await this.getZkProof({
            jwt: idToken,
            sub: decodedJwt.sub,
            iss: decodedJwt.iss,
            aud: decodedJwt.aud,
            userSalt: finalSalt,
            maxEpoch: parseInt(storedMaxEpoch),
            jwtRandomness: storedJwtRandomness,
            ephemeralPublicKey: extendedEphemeralPublicKey
        });

        return zkProof;
    }

    async getSuiLatestEpoch() {
        const suiClient = new SuiClient({ url: getFullnodeUrl(this.network) });
        const { epoch, epochDurationMs, epochStartTimestampMs } = await suiClient.getLatestSuiSystemState();
        return epoch;
    }
}

// 導出便利函數
export const createSuiZkLoginService = (options) => new SuiZkLoginService(options);